import { H1, Loading, Section, SubmitButton } from "@dgs/core";
import { Form, Formik } from "formik";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ContactViewGeneralSection } from "../contactUpdateInquiries/sections/ContactViewGeneralSection";
import { DeletionRequestSection } from "../contactUpdateInquiries/sections/DeletionRequestSection";
import { ConsentFormValues, ConsentIndexResource } from "../shared/types/consent";
import { ConsentFormFields } from "../shared/ui/ConsentFormFields";
import { useContactUpdateInquiry } from "./ContactUpdateInquiryProvider";
import { ContactViewAddressSection } from "./sections/ContactViewAddressSection";
import { ContactViewCompaniesSection } from "./sections/ContactViewCompaniesSection";
import { ContactViewEmailSection } from "./sections/ContactViewEmailSection";
import { ContactViewPersonalInformationSection } from "./sections/ContactViewPersonalInformationSection";
import { ContactViewPhoneNumberSection } from "./sections/ContactViewPhoneNumberSection";

const ContactUpdateContentContainer = styled.div`
	display: grid;
	grid-template-rows: max-content max-content 1fr;
	gap: ${({ theme }) => theme.spacing(5)};
	padding-top: ${({ theme }) => theme.spacing(5)};
	padding-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
`;

export const ContactUpdateDataContainer = styled.div`
	padding-bottom: ${({ theme }) => theme.spacing(5)};
	display: grid;
	gap: ${({ theme }) => theme.spacing(10)};
	row-gap: ${({ theme }) => theme.spacing(15)};
	grid-template-columns: 1fr 1fr;
	grid-template-areas:
		"personal personal"
		"email   email"
		"phone   phone"
		"companies companies"
		"address address"
		"general general";

	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-areas:
			"personal email"
			"phone companies"
			"address general";
	}
`;

export const toConsentErrors = (consents: ConsentIndexResource[], values: ConsentFormValues, t: TFunction) => {
	return consents.reduce((res, c) => {
		const id = c.id.toString();
		if (!values[id] && c.requiresConfirmation) {
			return {
				...res,
				[id]: t("Field is required"),
			};
		}
		return res;
	}, {});
};

export const ContactUpdateInquiryDetails = () => {
	const { t } = useTranslation();
	const { consents, createContactUpdateForContact, isLoading } = useContactUpdateInquiry();

	const initialConsentValues: ConsentFormValues = useMemo(
		() => consents.reduce((prev, curr) => ({ ...prev, [`${curr.id}`]: false }), {}),
		[consents],
	);

	return (
		<>
			<Section color="content">
				{isLoading && <Loading />}
				{!isLoading && (
					<ContactUpdateContentContainer>
						<H1>{t("Data update")}</H1>
						<p>
							{t(
								"So that we can continue to keep an eye on everything important for you, we ask you to check your data and update it if necessary. This will ensure that all information is up to date and that we can process your requests in the best possible way. Thank you for your help!",
							)}
						</p>
						<ContactUpdateDataContainer>
							<ContactViewPersonalInformationSection />
							<ContactViewEmailSection />
							<ContactViewPhoneNumberSection />
							<ContactViewCompaniesSection />
							<ContactViewAddressSection />
							<ContactViewGeneralSection />
						</ContactUpdateDataContainer>
						<Formik
							initialValues={initialConsentValues}
							onSubmit={createContactUpdateForContact}
							validate={(values) => toConsentErrors(consents, values, t)}
						>
							<Form>
								{!!consents.length && <ConsentFormFields consents={consents} />}
								<Actions>
									<SubmitButton color="primary">{t("Confirm")}</SubmitButton>
								</Actions>
							</Form>
						</Formik>
					</ContactUpdateContentContainer>
				)}
			</Section>
			<DeletionRequestSection />
		</>
	);
};
